// Pure, framework-agnostic helpers for Randrush lottery limit recovery.
//
// These functions implement the deterministic logic described in
// plans/ayg-lottery-limit-recovery-and-demand.md (Phase 3): stable draft-row
// identity, intent/attempt correlation lifecycle, parsing the structured
// LOTTERY_LIMIT_EXCEEDED contract produced by ryg-core, mapping those issues
// back to the exact selection that caused them, applying an editable suggestion
// without destroying the original requested demand, building an outbound payload
// exactly once (retry-safe), gating confirmation, and computing partial-acceptance
// residuals.
//
// They intentionally have no React/Axios dependency so they can be unit tested
// with `node --test`. Monetary values are kept in Bs (floats, 2 decimals) at the
// boundary and in integer minor units (1 Bs = 100 minor) when mirroring the
// backend *_minor fields. The bet step matches ryg-core LotteryBetMultiple (10 Bs
// = 1000 minor units).

export const BET_STEP_MINOR = 1000; // 10 Bs increment (LotteryBetMultiple).
export const MINOR_PER_BS = 100;

// Recovery status for a single payable draft row.
export const ROW_OK = "ok";
export const ROW_ADJUSTABLE = "adjustable"; // over-limit: an amount change can fix it.
export const ROW_ADJUSTED = "adjusted"; // a suggestion/edit resolved an over-limit row.
export const ROW_UNAVAILABLE = "unavailable"; // sold out: no capacity now.
export const ROW_BELOW_MIN = "below_minimum"; // remaining capacity is under the bet step.

// Whole-response outcome kinds derived from the backend `code`/HTTP status.
export const OUTCOME_SUCCESS = "success";
export const OUTCOME_LIMIT = "limit"; // structured, reviewable limit rejection.
export const OUTCOME_LIMIT_LEGACY = "limit_legacy"; // legacy `items`-only rejection.
export const OUTCOME_CONFLICT = "conflict"; // attempt id reused for a different play.
export const OUTCOME_UNRESOLVED = "unresolved"; // prior attempt still in process.
export const OUTCOME_STORE_UNAVAILABLE = "store_unavailable"; // strict pre-sale 503.
export const OUTCOME_CORRELATION_INVALID = "correlation_invalid";
export const OUTCOME_NETWORK = "network"; // no response at all (uncertain).
export const OUTCOME_SERVER = "server"; // 5xx after the sale may have happened.
export const OUTCOME_GENERIC = "generic"; // other definitive error.
export const OUTCOME_UNKNOWN = "unknown"; // cannot be safely classified.

// --- Money -------------------------------------------------------------------

export function bsToMinor(bs) {
  if (typeof bs !== "number" || !Number.isFinite(bs) || bs < 0) {
    throw new Error(`invalid Bs amount: ${bs}`);
  }
  const scaled = bs * MINOR_PER_BS;
  const rounded = Math.round(scaled);
  if (Math.abs(scaled - rounded) > 1e-4) {
    throw new Error(`amount ${bs} has invalid sub-cent precision`);
  }
  return rounded;
}

export function minorToBs(minor) {
  if (!Number.isInteger(minor)) {
    throw new Error(`invalid minor amount: ${minor}`);
  }
  return Math.round(minor) / MINOR_PER_BS;
}

// Floor a minor amount to the nearest allowed bet step. Never invents capacity:
// returns 0 when the amount is below one step.
export function floorToStepMinor(minor, stepMinor = BET_STEP_MINOR) {
  if (!Number.isInteger(minor) || minor <= 0) return 0;
  return Math.floor(minor / stepMinor) * stepMinor;
}

// --- Identifiers -------------------------------------------------------------

export function defaultIdGenerator() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  // Keep the API contract valid even in older runtimes without randomUUID.
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function makeIdFactory(gen) {
  if (typeof gen === "function") return gen;
  return defaultIdGenerator;
}

// --- Stable row identity -----------------------------------------------------

// A canonical key for a selection identity. It uses the draw UUID and the exact
// selection string (sign_id for figures, the padded number string for numbers),
// never a numeric coercion, so "0", "00" and "000" stay distinct.
export function selectionKey(row) {
  const sel = row.selectionType === "figure" ? `f:${row.signId ?? ""}` : `n:${row.number ?? ""}`;
  return `${row.drawId}::${sel}`;
}

// Ensure a draft row carries a stable rowId (== client_bet_id). Existing ids are
// preserved (edits keep identity); a row without one gets a fresh id (new bet).
export function ensureRowId(row, gen) {
  if (row && typeof row.rowId === "string" && row.rowId.length > 0) return row;
  return { ...row, rowId: makeIdFactory(gen)() };
}

// Map the visual fichas board (placedBets) to a flat array of payable draft rows.
// Group/visual-only rows are preserved but flagged so they never enter amounts or
// payloads. Ficha value sums become the row amount; a per-selection running total
// is used so repeated fichas on the same animal aggregate into one selection.
export function draftRowsFromBoard(board, ctx) {
  const gen = makeIdFactory(ctx?.genId);
  const { drawId, selectionType } = ctx || {};
  const rows = [];
  for (const bet of board || []) {
    if (bet?.isGroupBetVisual) {
      rows.push({
        rowId: bet.rowId || gen(),
        drawId: bet.drawId || drawId,
        selectionType: bet.selectionType || selectionType,
        number: bet.number ?? bet.animalId ?? null,
        signId: bet.signId ?? null,
        amountBs: 0,
        originalAmountBs: 0,
        isGroupBetVisual: true,
      });
      continue;
    }
    const fichas = Array.isArray(bet?.fichas) ? bet.fichas : [];
    const amount = fichas.reduce((sum, f) => sum + (Number(f?.value) || 0), 0);
    const selType = bet?.selectionType || selectionType;
    const number = selType === "figure" ? bet?.number ?? null : bet?.number ?? bet?.animalId ?? null;
    const signId = selType === "figure" ? bet?.signId ?? bet?.animalId ?? null : bet?.signId ?? null;
    rows.push({
      rowId: bet?.rowId || gen(),
      drawId: bet?.drawId || drawId,
      selectionType: selType,
      number: number != null ? String(number) : null,
      signId: signId != null ? String(signId) : null,
      amountBs: amount,
      originalAmountBs: bet?.originalAmountBs != null ? bet.originalAmountBs : amount,
      isGroupBetVisual: false,
    });
  }
  return rows;
}

// --- Intent / attempt lifecycle ---------------------------------------------

// A fresh purchase draft: brand-new intent and attempt.
export function beginIntent(gen) {
  const g = makeIdFactory(gen);
  return { intentId: g(), attemptId: g() };
}

// An explicitly confirmed revision/retry after a DEFINITIVE rejection keeps the
// intent but mints a new attempt so the backend records a distinct demand.
export function newAttemptForRevision(state, gen) {
  const g = makeIdFactory(gen);
  return { intentId: state?.intentId || g(), attemptId: g() };
}

// An uncertain technical retry (network drop / pre-sale 503) MUST reuse the same
// attempt id so the backend idempotency guard prevents a double sale.
export function keepAttemptForRetry(state) {
  return { intentId: state.intentId, attemptId: state.attemptId };
}

// Discarding the draft / completing a purchase resets correlation for the next
// separate purchase.
export function resetIntent(gen) {
  return beginIntent(gen);
}

// --- Outcome classification --------------------------------------------------

function backendPayload(err) {
  if (!err || typeof err !== "object") return null;
  return err.response?.data || err.data || null;
}

// Classify a raw error thrown by useCreateTicket into a normalized outcome. The
// returned object never fabricates availability.
export function classifyTicketError(err) {
  if (!err) return { kind: OUTCOME_UNKNOWN, retryable: false, definitive: false };

  // Explicit network failure (the private interceptor flags these). We cannot know
  // whether a sale happened, so an uncertain retry must reuse the SAME attempt id.
  if (err.isNetworkError === true || err.message === "networkError") {
    return {
      kind: OUTCOME_NETWORK,
      retryable: true,
      definitive: false, // must NOT mint a new attempt; reuse same attempt id.
      reuseAttempt: true,
      message: "Sin conexión. Revisa tus jugadas antes de reintentar.",
    };
  }
  // A thrown value with no HTTP response and no network flag is unrecognizable:
  // never treat it as a fresh, safe retry.
  if (!err.response) {
    return {
      kind: OUTCOME_UNKNOWN,
      retryable: false,
      definitive: false,
      uncertain: true,
      blockResubmit: true,
    };
  }

  const status = err.response.status;
  const payload = backendPayload(err) || {};
  const code = payload.code;

  if (status === 503 && code === "LOTTERY_DEMAND_STORE_UNAVAILABLE") {
    return {
      kind: OUTCOME_STORE_UNAVAILABLE,
      retryable: true,
      definitive: false,
      reuseAttempt: true, // no sale occurred; safe to replay same attempt.
      message: payload.message || "No se pudo registrar tu apuesta. Inténtalo de nuevo.",
    };
  }
  if (status === 409 && code === "LOTTERY_ATTEMPT_UNRESOLVED") {
    return {
      kind: OUTCOME_UNRESOLVED,
      retryable: false,
      definitive: false,
      blockResubmit: true, // direct the player to ticket history.
      message: payload.message || "Tu intento anterior sigue en proceso; revisa tus jugadas.",
    };
  }
  if (status === 409 && code === "LOTTERY_ATTEMPT_CONFLICT") {
    return {
      kind: OUTCOME_CONFLICT,
      retryable: true,
      definitive: true,
      needsNewAttempt: true,
      message: payload.message || "Este intento ya se usó para otra jugada.",
    };
  }
  if (status === 400 && code === "LOTTERY_CORRELATION_ID_INVALID") {
    return {
      kind: OUTCOME_CORRELATION_INVALID,
      retryable: false,
      definitive: true,
      message: payload.message || "Identificador de apuesta inválido.",
    };
  }
  if (status === 400 && code === "LOTTERY_LIMIT_EXCEEDED") {
    const issues = Array.isArray(payload.limit_issues) ? payload.limit_issues : null;
    return {
      kind: issues ? OUTCOME_LIMIT : OUTCOME_LIMIT_LEGACY,
      retryable: false,
      definitive: true,
      issues,
      items: payload.items || null,
      intentId: payload.intent_id || null,
      attemptId: payload.attempt_id || null,
      message: payload.message || "No se pudo procesar el ticket por exceder limite(s).",
    };
  }
  // No structured code but legacy items map present.
  if (status === 400 && payload.items && typeof payload.items === "object") {
    return {
      kind: OUTCOME_LIMIT_LEGACY,
      retryable: false,
      definitive: true,
      items: payload.items,
      message: payload.message || "No se pudo procesar el ticket por exceder limite(s).",
    };
  }
  if (typeof status === "number" && status >= 500) {
    // After the provider may have sold, treat as uncertain (never auto resubmit).
    return {
      kind: OUTCOME_SERVER,
      retryable: false,
      definitive: false,
      uncertain: true,
      blockResubmit: true,
      message: payload.message || "Error del servidor. Revisa tus jugadas antes de reintentar.",
    };
  }
  if (status === 401) {
    return { kind: OUTCOME_GENERIC, retryable: false, definitive: true, message: "Sesión expirada. Inicia sesión nuevamente." };
  }
  if (payload.message) {
    return { kind: OUTCOME_GENERIC, retryable: false, definitive: true, message: payload.message };
  }
  return { kind: OUTCOME_UNKNOWN, retryable: false, definitive: false, uncertain: true, blockResubmit: true };
}

// --- Issue mapping -----------------------------------------------------------

function indexIssuesByRowId(issues) {
  const map = new Map();
  for (const issue of issues || []) {
    const refs = Array.isArray(issue.source_bets) ? issue.source_bets : [];
    for (const ref of refs) {
      if (ref && typeof ref.client_bet_id === "string" && ref.client_bet_id) {
        // Keep the first (aggregate) issue for a row; multiple source bets of a
        // single aggregate selection map to the same issue object.
        if (!map.has(ref.client_bet_id)) map.set(ref.client_bet_id, issue);
      }
    }
  }
  return map;
}

function reasonToStatus(reason) {
  if (reason === "over_limit") return ROW_ADJUSTABLE;
  if (reason === "sold_out") return ROW_UNAVAILABLE;
  if (reason === "below_minimum") return ROW_BELOW_MIN;
  return ROW_ADJUSTABLE;
}

// Attach the structured limit issues to the draft rows they refer to, purely by
// client_bet_id (never by number alone). Rows without a matching issue are
// unaffected (kept normally). When issues reference unknown row ids the mapping is
// reported as uncertain so callers never guess availability for a real row.
export function applyLimitIssues(rows, issues) {
  const byRow = indexIssuesByRowId(issues);
  const matchedRowIds = new Set();
  const next = (rows || []).map(row => {
    if (row.isGroupBetVisual) return { ...row };
    const issue = byRow.get(row.rowId);
    if (!issue) {
      return { ...row, recovery: { status: ROW_OK } };
    }
    matchedRowIds.add(row.rowId);
    const status = reasonToStatus(issue.reason);
    return {
      ...row,
      recovery: {
        status,
        availableBs: Number.isInteger(issue.available_minor) ? minorToBs(issue.available_minor) : null,
        suggestedBs: Number.isInteger(issue.suggested_minor) && issue.suggested_minor > 0 ? minorToBs(issue.suggested_minor) : null,
        requestedBs: Number.isInteger(issue.requested_minor) ? minorToBs(issue.requested_minor) : row.amountBs,
        reason: issue.reason,
        constraints: Array.isArray(issue.constraints) ? issue.constraints : [],
      },
    };
  });
  const unknownIssueRows = [...byRow.keys()].filter(id => !matchedRowIds.has(id));
  return { rows: next, mappingUncertain: unknownIssueRows.length > 0, unknownIssueRows };
}

// Clear recovery overlays (e.g. the draft became valid again / new attempt).
export function clearRecovery(rows) {
  return (rows || []).map(r => (r.recovery ? { ...r, recovery: { status: r.isGroupBetVisual ? undefined : ROW_OK } } : { ...r }));
}

// --- Suggestions & edits -----------------------------------------------------

// Apply the backend suggestion as the row's payable amount. The original requested
// demand is preserved for auditing and for reporting; the row is marked adjusted.
export function applySuggestion(rows, rowId) {
  return (rows || []).map(row => {
    if (row.rowId !== rowId || !row.recovery || row.recovery.suggestedBs == null) return row;
    const suggested = floorToStepMinor(bsToMinor(row.recovery.suggestedBs));
    if (suggested <= 0) return row;
    return {
      ...row,
      amountBs: minorToBs(suggested),
      recovery: { ...row.recovery, status: ROW_ADJUSTED, originalBs: row.originalAmountBs },
    };
  });
}

// Player manually edits a row's amount (keeps original demand untouched).
export function setRowAmount(rows, rowId, amountBs) {
  return (rows || []).map(row => {
    if (row.rowId !== rowId) return row;
    const recovery = row.recovery
      ? { ...row.recovery, status: row.recovery.status === ROW_ADJUSTED ? ROW_ADJUSTED : ROW_OK, edited: true }
      : { status: ROW_OK };
    return { ...row, amountBs: Number(amountBs), recovery };
  });
}

// Remove a row by stable id (never by visual index) so sorting/pagination stay
// correct.
export function removeRowById(rows, rowId) {
  return (rows || []).filter(row => row.rowId !== rowId);
}

// --- Payable selection / totals / payload ------------------------------------

export function isPayable(row) {
  if (!row || row.isGroupBetVisual) return false;
  if (row.recovery?.sold) return false; // locked as already sold
  const s = row.recovery?.status ?? ROW_OK;
  return s === ROW_OK || s === ROW_ADJUSTED;
}

export function payableRows(rows) {
  return (rows || []).filter(isPayable);
}

export function blockedRows(rows) {
  return (rows || []).filter(
    r => !r.isGroupBetVisual && r.recovery && [ROW_UNAVAILABLE, ROW_BELOW_MIN, ROW_ADJUSTABLE].includes(r.recovery.status)
  );
}

export function hasUnresolved(rows) {
  return (rows || []).some(r => r.recovery && r.recovery.status === ROW_ADJUSTABLE);
}

export function payableTotalBs(rows) {
  return payableRows(rows).reduce((sum, r) => sum + (Number(r.amountBs) || 0), 0);
}

// Build the outbound request exactly once from the reviewed payable draft. Pure
// and deterministic: calling it twice with the same rows yields an identical
// payload, and retries never accumulate duplicate tickets/bets.
export function buildSubmissionPayload(rows, ctx) {
  const { currency, intentId, attemptId, isAnimalGame, numberPadWidth = 3 } = ctx || {};
  const byDraw = new Map();
  for (const row of payableRows(rows)) {
    if (!byDraw.has(row.drawId)) byDraw.set(row.drawId, []);
    const amount = Number(row.amountBs) || 0;
    if (isAnimalGame || row.selectionType === "figure") {
      byDraw.get(row.drawId).push({
        numbers: [],
        amount,
        sign_id: row.signId != null ? String(row.signId) : null,
        client_bet_id: row.rowId,
      });
    } else {
      // Preserve the exact per-game zero padding (Terminal uses 2 digits, Triple 3).
      const padded = row.number != null ? String(row.number).padStart(numberPadWidth, "0") : null;
      byDraw.get(row.drawId).push({
        numbers: padded ? [padded] : [],
        amount,
        sign_id: null,
        client_bet_id: row.rowId,
      });
    }
  }
  const items = [...byDraw.entries()].map(([drawId, bets]) => ({ draw_id: drawId, bets }));
  const payload = { currency, items };
  if (intentId) payload.intent_id = intentId;
  if (attemptId) payload.attempt_id = attemptId;
  return payload;
}

// --- Confirmation gating -----------------------------------------------------

// Confirmation is enabled only when a payable draft exists, every over-limit row
// has been resolved (adjusted/edited/removed), no request is pending, and nothing
// is locked as already sold / uncertain.
export function canConfirm(rows, state) {
  const opts = state || {};
  if (opts.isPending) return false;
  if (opts.locked) return false; // submitted snapshot locked during pending.
  if (opts.blockResubmit) return false;
  if (hasUnresolved(rows)) return false;
  if (payableRows(rows).length === 0) return false;
  return true;
}

// --- Partial / accepted outcome mapping --------------------------------------

function findAcceptedForIndex(acceptedBets, index) {
  return (acceptedBets || []).find(b => Number(b.index) === index);
}

// Map a purchase response onto draft rows. Fully accepted rows become sold (locked
// out of any later draft). Partially accepted rows keep only the unaccepted residual
// as payable. Rejected (definitively unaccepted within a successful sale) rows stay
// payable for a subsequent draft. If accepted/rejected sets cannot be matched to the
// submitted draft order with confidence, the result is `uncertain` so callers disable
// resubmission and direct the player to ticket history.
export function applyPurchaseOutcome(rows, response) {
  const data = response?.data?.data || response?.data || response || {};
  const payable = payableRows(rows);
  const acceptedBets = Array.isArray(data.accepted_bets) ? data.accepted_bets : null;
  const rejectedBets = Array.isArray(data.rejected_bets) ? data.rejected_bets : null;
  const totalAccepted = Number(data.total_accepted);
  const totalRequested = Number(data.total_requested);

  // If we have per-bet outcomes they must cover the payable draft exactly by index.
  const hasPerBet = acceptedBets != null || rejectedBets != null;
  const coveredCount = (acceptedBets?.length || 0) + (rejectedBets?.length || 0);
  const uncertainByCoverage = hasPerBet && coveredCount > 0 && coveredCount !== payable.length;

  // Aggregate-only AYG path: AYG is self-banked and accepts full amounts. If the
  // totals confirm everything was accepted and there are no rejected bets, we can
  // mark all payable rows sold with confidence.
  const allAcceptedAggregate = !hasPerBet && Number.isFinite(totalAccepted) && Number.isFinite(totalRequested) && Math.abs(totalAccepted - totalRequested) < 1e-9 && (!rejectedBets || rejectedBets.length === 0);

  const acceptedByIndex = new Map();
  for (const b of acceptedBets || []) acceptedByIndex.set(Number(b.index), b);
  const rejectedIdx = new Set((rejectedBets || []).map(b => Number(b.index)));

  let soldCount = 0;
  let residualCount = 0;
  let unacceptedCount = 0;
  const next = (rows || []).map(row => {
    if (row.isGroupBetVisual) return { ...row };
    if (!isPayable(row)) return { ...row };
    const draftIndex = payable.findIndex(p => p.rowId === row.rowId);

    if (allAcceptedAggregate) {
      soldCount += 1;
      return { ...row, recovery: { status: ROW_OK, sold: true } };
    }

    if (hasPerBet) {
      if (rejectedIdx.has(draftIndex)) {
        unacceptedCount += 1;
        return { ...row, recovery: { status: ROW_OK } }; // keep payable for next draft
      }
      const acc = findAcceptedForIndex([...(acceptedBets || [])], draftIndex);
      if (acc) {
        const requested = Number(acc.requested_amount);
        const accepted = Number(acc.accepted_amount);
        if (Number.isFinite(requested) && Number.isFinite(accepted) && accepted < requested - 1e-9) {
          residualCount += 1;
          return { ...row, amountBs: Math.round((requested - accepted) * 100) / 100, recovery: { status: ROW_OK, soldAmount: accepted } };
        }
        soldCount += 1;
        return { ...row, recovery: { status: ROW_OK, sold: true } };
      }
    }

    // Cannot confidently determine this row's fate.
    return { ...row, recovery: { status: ROW_OK, unknown: true } };
  });

  const anyUnknown = next.some(r => r.recovery?.unknown);
  const uncertain = uncertainByCoverage || anyUnknown || (!allAcceptedAggregate && !hasPerBet);

  return {
    rows: next,
    uncertain,
    soldCount,
    residualCount,
    unacceptedCount,
    acceptedAmount: Number.isFinite(totalAccepted) ? totalAccepted : null,
  };
}

// Canonical, order-independent signature of a payable draft in integer minor
// units. Two submissions share a signature iff they would produce an
// economically identical request (same selections and amounts), regardless of
// row order. Used to distinguish an unchanged technical retry (reuse the same
// attempt id) from a player revision (mint a new attempt under the same intent).
export function payloadSignature(rows) {
  const parts = payableRows(rows)
    .map(r => {
      let minor;
      try {
        minor = bsToMinor(Number(r.amountBs) || 0);
      } catch {
        minor = Math.round((Number(r.amountBs) || 0) * MINOR_PER_BS);
      }
      const sel = r.selectionType === "figure" ? `f:${r.signId ?? ""}` : `n:${r.number ?? ""}`;
      return `${r.drawId}|${sel}|${minor}`;
    })
    .sort();
  return parts.join(";");
}
